/*
 * OCW A/B test renderer and exposure tracking
 * - Renders control/treatment content
 * - Sends exposure through experimentClient.exposure(flagKey)
 * - Deduplicates exposure, Tealium events, requests, and renders
 */
import { getCookie, setCookie } from "./cookiebanner";
import { personalizationHeroBanner } from "./personalization-banner";

// ===== Shared Experiment Tracking State =====
window.__OCW_EXPERIMENT_TRACKING_STATE__ =
  window.__OCW_EXPERIMENT_TRACKING_STATE__ || {
    amplitudeExposures: new Set(),
    tealiumEvents: new Set(),
    pendingVariantRequests: new Set(),
    renderedVariants: new Set(),
  };

const experimentTrackingState = window.__OCW_EXPERIMENT_TRACKING_STATE__;
const sentAmplitudeExposures = experimentTrackingState.amplitudeExposures;
const sentExperimentEvents = experimentTrackingState.tealiumEvents;
const pendingVariantRequests = experimentTrackingState.pendingVariantRequests;
const renderedVariants = experimentTrackingState.renderedVariants;

// ===== Small Utilities =====
function escapeSelectorValue(value) {
  const raw = String(value || '');
  return window.CSS && typeof window.CSS.escape === 'function'
    ? window.CSS.escape(raw)
    : raw.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function getComponentSelector(component, variantOnly = false) {
  const escaped = escapeSelectorValue(component);
  const base = `[data-component="${escaped}"]`;

  return variantOnly
    ? `${base}[data-abtesttype]:not([data-abtesttype="control"])`
    : base;
}

function getExperimentClient() {
  return window.OCW_ACTIVE_EXPERIMENTS
    && window.OCW_ACTIVE_EXPERIMENTS.client
    ? window.OCW_ACTIVE_EXPERIMENTS.client
    : null;
}

// ===== Viewport Visibility Helper =====
function onVisible(el, callback) {
  if (!el) return;

  const obs = new IntersectionObserver(function handleIntersection(entries) {
    for (let i = 0; i < entries.length; i += 1) {
      if (entries[i].isIntersecting) {
        try {
          callback();
        } finally {
          obs.disconnect();
        }
        return;
      }
    }
  }, { threshold: 0.2 });

  obs.observe(el);
}

function isElementInViewport(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return false;

  const rect = el.getBoundingClientRect();
  const viewHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const viewWidth = window.innerWidth || document.documentElement.clientWidth || 0;

  return rect.bottom > 0
    && rect.right > 0
    && rect.top < viewHeight
    && rect.left < viewWidth;
}

// ===== Tealium Tracking =====
function ensureTealiumData(experimentKey, flagKey, variant) {
  // Kept for backward compatibility with callers that may use this helper.
  // Experiment values are intentionally not persisted into window.utag.data;
  // they are sent only on the corresponding utag.link payload.
  return {
    experiment_key: experimentKey || null,
    experiment_flag_key: flagKey || null,
    experiment_variant: variant || null,
  };
}

function utagLink(payload) {
  if (window.utag && typeof window.utag.link === 'function') {
    try {
      window.utag.link(payload);
      console.log('[TL] Sent utag.link:', payload);
    } catch (e) {
      console.warn('[TL] utag.link failed', e);
    }
  } else {
    console.info('[TL] utag.link unavailable (skipping)');
  }
}

function buildTealiumPayload(o, appType) {
  const isExposure = o.eventType === 'exposure';
  const action = isExposure ? 'experiment exposure' : 'experiment evaluated';
  const suffix = isExposure ? 'exposure' : 'shown';

  return Object.assign({
    application_type: appType,
    event_category: appType,
    event_action: action,
    page_location: window.location.pathname || '/',
    tealium_event: `experiment_${o.flagKey}_${o.variant}_${suffix}`,
    event_label: `experiment ${o.flagKey} ${o.variant} ${action}`,
    experiment_key: o.experimentKey,
    experiment_flag_key: o.flagKey,
    experiment_variant: o.variant,
  }, o.extraProps || {});
}

function trackTealiumEvent(flagKey, variant, experimentKey, eventType, extraProps) {
  const normalizedEventType = eventType === 'exposure' ? 'exposure' : 'evaluated';
  const cacheKey = `${experimentKey || 'unknown'}|${flagKey}|${variant}|${normalizedEventType}`;

  if (sentExperimentEvents.has(cacheKey)) {
    console.info('[TL] Duplicate event skipped:', cacheKey);
    return;
  }

  sentExperimentEvents.add(cacheKey);

  const utagData = (window.utag && window.utag.data) || {};
  const applicationType = utagData.application_type || 'default_application_type';

  console.log(`[TL] ${normalizedEventType.toUpperCase()} event`, {
    experimentKey,
    flagKey,
    variant,
  });

  utagLink(buildTealiumPayload({
    flagKey,
    variant,
    experimentKey,
    eventType: normalizedEventType,
    extraProps,
  }, applicationType));
}

// ===== Amplitude Experiment Exposure =====
function trackAmplitudeExposure(flagKey, variant, experimentKey) {
  const dedupKey = `${experimentKey || 'unknown'}|${flagKey}|${variant}`;

  if (sentAmplitudeExposures.has(dedupKey)) {
    console.info('[AMP] Duplicate exposure skipped:', dedupKey);
    return false;
  }

  const experimentClient = getExperimentClient();
  if (!experimentClient || typeof experimentClient.exposure !== 'function') {
    console.warn('[EXP] Experiment client unavailable for exposure:', {
      experimentKey,
      flagKey,
      variant,
    });
    return false;
  }

  try {
    // automaticExposureTracking is disabled, so this explicit call records
    // exposure only after the selected experience is actually visible.
    experimentClient.exposure(flagKey);
    sentAmplitudeExposures.add(dedupKey);

    console.log('[AMP] Exposure sent through Experiment SDK:', {
      experimentKey,
      flagKey,
      variant,
    });

    return true;
  } catch (e) {
    console.warn('[EXP] experimentClient.exposure failed', e);
    return false;
  }
}

// ===== Exposure Setup on Visibility =====
function trackExposureOnVisible(target, flagKey, variant, experimentKey) {
  if (!target || (typeof target !== 'string' && !(target instanceof Element))) {
    console.info('[EXP] No valid exposure target:', flagKey);
    return;
  }

  try {
    const existing = typeof target === 'string'
      ? document.querySelector(target)
      : target;

    if (!existing) {
      console.info('[EXP] Exposure target not found:', {
        flagKey,
        variant,
        target,
      });
      return;
    }

    const targetLabel = typeof target === 'string'
      ? target
      : existing.outerHTML?.slice(0, 120) || existing.tagName;

    console.log('[EXP] Setting up exposure tracking:', {
      experimentKey,
      flagKey,
      variant,
      target: targetLabel,
    });

    function triggerExposure() {
      const amplitudeSent = trackAmplitudeExposure(
        flagKey,
        variant,
        experimentKey
      );

      // Tealium remains an independent reporting event. It must not be mapped
      // back to Amplitude as $exposure.
      trackTealiumEvent(
        flagKey,
        variant,
        experimentKey,
        'exposure',
        { amplitude_exposure_sent: amplitudeSent }
      );
    }

    if (isElementInViewport(existing)) {
      triggerExposure();
    } else {
      onVisible(existing, triggerExposure);
    }
  } catch (e) {
    console.warn('[EXP] trackExposureOnVisible failed', e);
  }
}

// ===== Expose Tracking Functions Globally =====
window.OCW_TRACKING = Object.assign(window.OCW_TRACKING || {}, {
  trackAmplitudeExposure,
  trackTealiumEvent,
  trackExposureOnVisible,
  ensureTealiumData,
});

export function showControl(compsList = []) {
  document.querySelectorAll('[data-abtesttype="control"]').forEach((elem) => {
    if (!compsList.includes(elem.dataset.component)) {
      elem.style.visibility = null;
    }
  });
}

function normalizeComponents(componentDefinition) {
  if (Array.isArray(componentDefinition)) {
    return componentDefinition
      .filter((component) => typeof component === 'string')
      .map((component) => component.trim())
      .filter(Boolean);
  }

  if (typeof componentDefinition === 'string' && componentDefinition.trim()) {
    return [componentDefinition.trim()];
  }

  return [];
}

function findControlElement(component) {
  const selector = `${getComponentSelector(component)}[data-abtesttype="control"]`;
  return document.querySelector(selector);
}

export function abTestVTwo(ocw_experiment) {
  const experimentComps = Array.from(document.querySelectorAll('[data-component]'));
  const activeComponents = [];
  const currLang = document.documentElement.lang;
  const pageComponents = Array.from(new Set(
    experimentComps
      .map((elem) => elem.dataset.component)
      .filter(Boolean)
  ));

  if (!experimentComps.length) {
    showControl();
    return;
  }

  if (!ocw_experiment || !Object.keys(ocw_experiment).length) {
    showControl();
    return;
  }

  let anyExperimentMatched = false;

  Object.keys(ocw_experiment).forEach((flagKey) => {
    const experiment = ocw_experiment[flagKey] || {};
    const payload = experiment.payload || {};
    const variant = experiment.key;
    const experimentKey = experiment.expKey || flagKey;
    const configuredComponents = normalizeComponents(payload.component);
    const matchedComponents = configuredComponents.filter((component) =>
      pageComponents.includes(component)
    );

    if (!matchedComponents.length || !variant) return;

    anyExperimentMatched = true;

    matchedComponents.forEach((component) => {
      if (!activeComponents.includes(component)) {
        activeComponents.push(component);
      }
    });

    if (variant === 'control') {
      matchedComponents.forEach((component) => {
        const controlElement = findControlElement(component);
        if (controlElement) {
          controlElement.style.visibility = null;
          trackExposureOnVisible(
            controlElement,
            flagKey,
            variant,
            experimentKey
          );
        }
      });
      return;
    }

    matchedComponents.forEach((component) => {
      document.querySelectorAll(
        `${getComponentSelector(component)}[data-abtesttype="control"]`
      ).forEach((elem) => {
        elem.style.visibility = 'hidden';
      });
    });

    const params = {
      components: JSON.stringify(matchedComponents),
      abtestype: variant,
      lang: currLang,
    };

    handleVariant(
      params,
      flagKey,
      variant,
      experimentKey
    );
  });

  if (!anyExperimentMatched) {
    showControl();
  } else {
    showControl(activeComponents);
  }
}
function initializeABTests() {
  const currentVariants =
    window.OCW_ACTIVE_EXPERIMENTS?.variants || {};

  // Handles the case where the experiment finished loading
  // before this module was initialized.
  if (Object.keys(currentVariants).length > 0) {
    abTestVTwo(currentVariants);
    return;
  }

  // Handles the normal asynchronous initialization flow.
  document.addEventListener(
    'experiment_ready',
    function handleExperimentReady(event) {
      const variants = event.detail?.variants || {};
      abTestVTwo(variants);
    },
    { once: true }
  );
}

initializeABTests();

function handleVariant(params, flagKey, variant, experimentKey) {
  const requestKey = `${experimentKey || 'unknown'}|${flagKey}|${variant}`;

  if (renderedVariants.has(requestKey)) {
    console.info('[ABTEST] Variant already rendered:', requestKey);
    return;
  }

  if (pendingVariantRequests.has(requestKey)) {
    console.info('[ABTEST] Variant request already pending:', requestKey);
    return;
  }

  pendingVariantRequests.add(requestKey);

  const currUrl = `${window.location.origin}/wp-json/ocw-personalization/abtest/`;
  console.log('[ABTEST] fetch ->', currUrl, params);

  fetch(currUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`AB test request failed with status ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      if (!data || !Array.isArray(data)) {
        console.log('[ABTEST] No valid modules returned from API.');
        return;
      }

      let insertedAnyModule = false;

      data.forEach((module) => {
        if (!module || !module.component || typeof module.html !== 'string') {
          return;
        }

        const controlSelector = `${getComponentSelector(module.component)}[data-abtesttype="control"]`;
        const targetElem = document.querySelector(controlSelector)
          || document.querySelector(getComponentSelector(module.component));

        if (!targetElem) {
          console.warn('[ABTEST] No matching control element:', module.component);
          return;
        }

        const existingVariant = Array.from(
          document.querySelectorAll(getComponentSelector(module.component, true))
        ).pop();

        let insertedVariant = existingVariant || null;

        if (!insertedVariant) {
          const rawHtml = module.html.replace(/\\([\\"])/g, '$1');
          targetElem.insertAdjacentHTML('afterend', rawHtml);

          const insertedSibling = targetElem.nextElementSibling;
          insertedVariant = insertedSibling
            && insertedSibling.matches(getComponentSelector(module.component))
            && insertedSibling.dataset.abtesttype !== 'control'
            ? insertedSibling
            : Array.from(
              document.querySelectorAll(getComponentSelector(module.component, true))
            ).pop();

          console.log('[ABTEST] Component inserted:', module.component);
        }

        if (!insertedVariant) {
          console.warn('[ABTEST] Inserted variant could not be resolved:', module.component);
          return;
        }

        insertedAnyModule = true;

        if (targetElem.dataset.abtesttype === 'control') {
          targetElem.remove();
        }

        // The renderer owns exposure tracking because it knows the treatment
        // has been applied and can observe the actual inserted element.
        trackExposureOnVisible(
          insertedVariant,
          flagKey,
          variant,
          experimentKey
        );
      });

      if (insertedAnyModule) {
        renderedVariants.add(requestKey);
      }
    })
    .catch((error) => {
      console.error('[ABTEST] Error fetching components:', error);
    })
    .finally(() => {
      pendingVariantRequests.delete(requestKey);
    });
}

export function splitUrlTest() {
  const langtoggle = document.querySelectorAll('.wpml-ls-link');

  langtoggle.forEach((link) => {
    try {
      const url = new URL(link.href);
      if (
        link.href === 'https://oportun.com/'
        || link.href === 'https://oportunstg3.wpengine.com/'
        || link.href === 'https://oportundev3.wpengine.com/'
      ) {
        console.log(`${link.href}us`);
        link.href = `${link.href}us`;
      }
    } catch (e) {
      console.error('Error processing URL:', e);
    }
  });
}